import './priority-icon.scss';

/**
 * Generates a color for priority based on position in list.
 * Uses a smooth gradient from red (highest) through orange, yellow, lime to green (lowest).
 * @param {number} position - Current position (1-based)
 * @param {number} total - Total number of priorities
 * @returns {string} - Hex color string
 */
export const getPriorityColor = (position, total) => {
	// Extended color palette for smooth transitions
	const colorStops = [
		{ pos: 0, color: '#dc3545' },    // Red
		{ pos: 0.25, color: '#fd7e14' }, // Orange
		{ pos: 0.5, color: '#ffc107' },  // Yellow
		{ pos: 0.75, color: '#8bc34a' }, // Lime green
		{ pos: 1, color: '#28a745' },    // Green
	];

	if (total <= 1) return colorStops[0].color;

	// Calculate percentage (0 to 1) based on position
	const percentage = (position - 1) / (total - 1);

	// Find the two color stops to interpolate between
	let lowerStop = colorStops[0];
	let upperStop = colorStops[colorStops.length - 1];

	for (let i = 0; i < colorStops.length - 1; i++) {
		if (percentage >= colorStops[i].pos && percentage <= colorStops[i + 1].pos) {
			lowerStop = colorStops[i];
			upperStop = colorStops[i + 1];
			break;
		}
	}

	// Interpolate between the two colors
	const range = upperStop.pos - lowerStop.pos;
	const localPercentage = range === 0 ? 0 : (percentage - lowerStop.pos) / range;

	return interpolateColor(lowerStop.color, upperStop.color, localPercentage);
};

/**
 * Interpolates between two hex colors
 * @param {string} color1 - Starting hex color
 * @param {string} color2 - Ending hex color
 * @param {number} factor - Interpolation factor (0 to 1)
 * @returns {string} - Interpolated hex color
 */
const interpolateColor = (color1, color2, factor) => {
	const hex = (x) => {
		const hexVal = x.toString(16);
		return hexVal.length === 1 ? '0' + hexVal : hexVal;
	};

	const r1 = parseInt(color1.substring(1, 3), 16);
	const g1 = parseInt(color1.substring(3, 5), 16);
	const b1 = parseInt(color1.substring(5, 7), 16);

	const r2 = parseInt(color2.substring(1, 3), 16);
	const g2 = parseInt(color2.substring(3, 5), 16);
	const b2 = parseInt(color2.substring(5, 7), 16);

	const r = Math.round(r1 + factor * (r2 - r1));
	const g = Math.round(g1 + factor * (g2 - g1));
	const b = Math.round(b1 + factor * (b2 - b1));

	return `#${hex(r)}${hex(g)}${hex(b)}`;
};

/**
 * Dynamic Priority Icon component that displays position number with color coding.
 * Can be reused anywhere priority visualization is needed.
 *
 * @param {Object} props
 * @param {number} props.position - Current position (1-based)
 * @param {number} props.total - Total number of priorities
 * @param {number} [props.size=32] - Size of the icon in pixels
 * @param {boolean} [props.showNumber=true] - Whether to show the position number
 */
const PriorityIcon = ({ position, total, size = 32, showNumber = true }) => {
	const color = getPriorityColor(position, total);

	return (
		<div
			className="priority-icon"
			style={{
				backgroundColor: color,
				width: `${size}px`,
				height: `${size}px`,
				minWidth: `${size}px`,
				borderRadius: '50%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				color: 'white',
				fontWeight: 'bold',
				fontSize: `${size * 0.44}px`,
				boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
			}}
		>
			{showNumber && position}
		</div>
	);
};

export default PriorityIcon;
